import {
  useWSModal,
  WSActions,
  WSAlert,
  WSCard,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeEngagementStatus } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  queriesExternalBankAccounts,
  queriesPayeeRows,
  queriesPaymentCards,
  queriesPayouts
} from "@query";
import { externalBankAccountsApi, paymentCardsApi, payoutsApi } from "@api";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { WSErrorMessage } from "../../../../components/WSErrorMessage/WSErrorMessage";
import { useWSMutation } from "../../../../query/helpers";
import { useUserId } from "../../../../query/hooks/helpers";
import { QUERY_PAYMENT_CARDS } from "../../../../query/paymentCards/keys";
import { QUERY_PAYROLL_SETTINGS } from "../../../../query/payments/keys";
import { useQueryPayrollSettings } from "../../../../query/payments/queries";
import {
  QUERY_USERS_ACCOUNTS,
  QUERY_USERS_CLIENT
} from "../../../../query/users/keys";
import { useClientQuery } from "../../../../query/users/queries";
import {
  PAYMENT_METHOD_REPLACEMENT_ALERT_BODY,
  PAYMENT_METHOD_REPLACEMENT_ALERT_TITLE
} from "../../../../shared/constants/paymentMethodReplacement";
import { paymentsService } from "../../../../services/payments";
import { usersService } from "../../../../services/users";
import { WSServiceError } from "../../../../utils/serviceHelper";
import { PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYMENT_METHOD } from "../../paths";
import { useMethodLabelProps } from "../LabelMethod/useMethodLabelProps";
import { MethodType } from "./types";

type Props = {
  onClose: (isRemoved: boolean) => void;
  id: string;
  type: MethodType;
};

export const ModalRemove: React.FC<Props> = props => {
  const userId = useUserId();
  const queryPayrollSettings = useQueryPayrollSettings(userId);
  const queryPayoutSettings = useQueryOwnPayoutSettings();
  const queryClient = useClientQuery(userId);
  const history = useHistory();

  const isPayrollFundingMethod = useMemo(
    () =>
      queryPayrollSettings.data?.fundingSource?.fundingSourceId === props.id,
    [props.id, queryPayrollSettings.data?.fundingSource?.fundingSourceId]
  );

  const isPaymentMethod = useMemo(
    () =>
      queryClient.data?.profile.defaultPaymentMethod?.accountId === props.id ||
      queryClient.data?.profile.defaultPaymentMethod?.paymentMethodId ===
        props.id,
    [
      props.id,
      queryClient.data?.profile.defaultPaymentMethod?.accountId,
      queryClient.data?.profile.defaultPaymentMethod?.paymentMethodId
    ]
  );

  // Mirrors backend enforcement: a payer with any Active engagement must keep a method on file.
  // Only fetched when removing the default method (others can't orphan it).
  const activeEngagementsQuery = queriesPayeeRows.searchPayeeRows.useQuery(
    {
      filter: { engagements_status: { eq: PayeeEngagementStatus.ACTIVATED } },
      page: { size: 1 }
    },
    { enabled: isPaymentMethod }
  );
  const clientLoaded = !!queryClient.data;
  // Stable disabled action until client + policy state are known (no action swap, a11y).
  const isDeterminingRemoval =
    !clientLoaded ||
    (isPaymentMethod && activeEngagementsQuery.isInitialLoading);
  const mustReplaceBeforeRemoval =
    isPaymentMethod && (activeEngagementsQuery.data?.data?.length ?? 0) > 0;

  const isPayoutStandardMethod = useMemo(
    () => queryPayoutSettings.data?.standardDestination?.id === props.id,
    [props.id, queryPayoutSettings.data?.standardDestination?.id]
  );

  const isPayoutInstantMethod = useMemo(
    () => queryPayoutSettings.data?.instantDestination?.id === props.id,
    [props.id, queryPayoutSettings.data?.instantDestination?.id]
  );

  const usage = useMemo(() => {
    const list: string[] = [];
    if (isPaymentMethod) {
      list.push("payment method");
    }

    if (isPayoutInstantMethod) {
      list.push("instant payout method");
    }

    if (isPayoutStandardMethod) {
      list.push("standard payout method");
    }

    if (isPayrollFundingMethod) {
      list.push("payroll funding method");
    }

    return list;
  }, [
    isPaymentMethod,
    isPayoutInstantMethod,
    isPayoutStandardMethod,
    isPayrollFundingMethod
  ]);

  const [mutate, meta] = useWSMutation<
    void,
    WSServiceError,
    {
      isPaymentMethod?: boolean;
      isPayoutStandardMethod?: boolean;
      isPayoutInstantMethod?: boolean;
      isPayrollFundingMethod?: boolean;
    }
  >(
    async () => {
      const payoutSettingsId = queryPayoutSettings.data?.id;

      if (
        (isPayoutStandardMethod || isPayoutInstantMethod) &&
        payoutSettingsId
      ) {
        await payoutsApi.updatePayoutSettings({
          payoutSettingsId,
          payoutSettingsUpdate: {
            ...(isPayoutStandardMethod ? { standardDestination: null } : {}),
            ...(isPayoutInstantMethod ? { instantDestination: null } : {})
          }
        });
      }

      if (isPayrollFundingMethod) {
        await paymentsService.payrollSettings.update(userId, {
          fundingSource: null
        });
      }

      // Clear the default in the same (cards) / an earlier (non-cards) update so a rejection can't orphan it.
      if (props.type === "CreditCard") {
        const client = await usersService.client.get(userId);
        await usersService.client.update(userId, {
          clientId: userId,
          profile: {
            savedPaymentMethods: (client.profile.savedPaymentMethods || []).map(
              pm => (pm.paymentMethodId === props.id ? null : {})
            ),
            ...(isPaymentMethod
              ? {
                  defaultPaymentMethod: {
                    accountId: null,
                    paymentMethodId: null
                  }
                }
              : {})
          }
        });
      } else {
        if (isPaymentMethod) {
          await usersService.client.update(userId, {
            clientId: userId,
            profile: {
              defaultPaymentMethod: {
                accountId: null,
                paymentMethodId: null
              }
            }
          });
        }

        switch (props.type) {
          case "Account":
            await externalBankAccountsApi.deleteExternalBankAccount({
              bankAccountId: props.id
            });

            break;
          case "PaymentCard":
            await paymentCardsApi.deletePaymentCard({
              paymentCardId: props.id
            });

            break;
        }
      }
    },
    {
      onSuccess: () => props.onClose(true),
      dependencies: [
        // v1 keys kept for the readers still on v1: the client-payments portal
        // and Bookkeeping (bank accounts), and the client profile that holds the
        // default payment method + saved credit cards.
        QUERY_USERS_ACCOUNTS,
        QUERY_USERS_CLIENT,
        QUERY_PAYMENT_CARDS,
        QUERY_PAYROLL_SETTINGS,
        queriesExternalBankAccounts.listExternalBankAccounts.queryKeyBase,
        queriesExternalBankAccounts.getExternalBankAccount.queryKeyBase,
        queriesPaymentCards.listPaymentCards.queryKeyBase,
        queriesPaymentCards.getPaymentCard.queryKeyBase,
        queriesPayouts.listPayoutSettings.queryKeyBase,
        queriesPayouts.getPayoutSettings.queryKeyBase
      ]
    }
  );

  const { label } = useMethodLabelProps(props.id, props.type);

  return (
    <WSList gap="XL">
      <WSText.ParagraphSm color="gray600">
        Are you sure you want to remove this account?
      </WSText.ParagraphSm>

      <WSCard
        header={{
          label
        }}
      />

      {usage.length > 0 && (
        <WSAlert icon="alert" title="Account in use" theme="warning">
          This bank account is currently used as a {usage.join(", ")}. If you
          remove it, please select another account to set as the default(s).
        </WSAlert>
      )}

      {mustReplaceBeforeRemoval && (
        <WSAlert
          icon="info-circle"
          theme="info"
          title={PAYMENT_METHOD_REPLACEMENT_ALERT_TITLE}
        >
          {PAYMENT_METHOD_REPLACEMENT_ALERT_BODY}
        </WSAlert>
      )}

      <WSErrorMessage contextKey="AddPaymentMethod" error={meta.error} />

      <WSActions
        buttons={
          mustReplaceBeforeRemoval
            ? [
                {
                  kind: "Secondary",
                  label: "Cancel",
                  onClick: () => props.onClose(false)
                },
                {
                  label: "Add payment method",
                  onClick: () => {
                    props.onClose(false);
                    history.push(
                      PATH_SETTINGS_PAYMENT_METHODS_ADD_PAYMENT_METHOD
                    );
                  }
                }
              ]
            : [
                {
                  kind: "Secondary",
                  label: "Cancel",
                  onClick: () => props.onClose(false)
                },
                {
                  destructive: true,
                  label: "Remove",
                  onClick: () => {
                    mutate({
                      isPaymentMethod,
                      isPayoutStandardMethod,
                      isPayoutInstantMethod,
                      isPayrollFundingMethod
                    });
                  },
                  // Disabled until client + policy state is known.
                  loading: meta.isLoading || isDeterminingRemoval
                }
              ]
        }
      />
    </WSList>
  );
};

export const useModalRemove = () =>
  useWSModal(ModalRemove, {
    title: "Remove account",
    size: "S"
  });
